/**
 * Subscription-based access-check utilities.
 *
 * Centralises the logic that decides which modules / elementary items
 * a user can see based on their subscription_plan_ids array.
 */

import type { User, ModuleId } from '../types';
import {
  MODULE_ACCESS_BY_PLAN,
  ELEMENTARY_ACCESS_BY_PLAN,
} from '../services/api_subscriptions';
import type { ElementaryItemId } from '../services/api_subscriptions';
import { hasRole } from './roleUtils';

// ---------------------------------------------------------------------------
// Module access helpers
// ---------------------------------------------------------------------------

/**
 * Given a list of plan IDs, return the union of all accessible modules.
 */
export function getAccessibleModules(planIds: string[]): ModuleId[] {
  const set = new Set<ModuleId>();
  for (const planId of planIds) {
    const modules = MODULE_ACCESS_BY_PLAN[planId];
    if (modules) {
      for (const m of modules) set.add(m);
    }
  }
  return Array.from(set);
}

/**
 * Check whether a user has access to a specific module.
 * Admins always pass. Non-admins must have an active subscription that
 * includes the requested module.
 */
export function hasModuleAccess(user: User | null, moduleId: ModuleId): boolean {
  if (!user) return false;
  if (hasRole(user, 'admin')) return true;

  const status = user.subscription_status;
  if (status !== 'active' && status !== 'trialing') return false;

  const planIds = user.subscription_plan_ids || [];
  return getAccessibleModules(planIds).includes(moduleId);
}

// ---------------------------------------------------------------------------
// Elementary item access helpers
// ---------------------------------------------------------------------------

/**
 * Given a list of plan IDs, return the union of all accessible elementary items.
 */
export function getAccessibleElementaryItems(planIds: string[]): ElementaryItemId[] {
  const set = new Set<ElementaryItemId>();
  for (const planId of planIds) {
    const items = ELEMENTARY_ACCESS_BY_PLAN[planId];
    if (items) {
      for (const i of items) set.add(i);
    }
  }
  return Array.from(set);
}

/**
 * Check whether a user has access to a specific elementary item.
 * Admins always pass.
 */
export function hasElementaryItemAccess(user: User | null, itemId: ElementaryItemId): boolean {
  if (!user) return false;
  if (hasRole(user, 'admin')) return true;

  const status = user.subscription_status;
  if (status !== 'active' && status !== 'trialing') return false;

  const planIds = user.subscription_plan_ids || [];
  return getAccessibleElementaryItems(planIds).includes(itemId);
}

// ---------------------------------------------------------------------------
// Sidebar group-name → ModuleId mapping
// ---------------------------------------------------------------------------

export const GROUP_NAME_TO_MODULE: Record<string, ModuleId> = {
  'Budget Planner': 'budget',
  'Budget Guides': 'budget',
  'Food Truck': 'food_truck',
  'Food Truck Guides': 'food_truck',
  'Credit Assessment': 'credit_assessment',
  'Credit Assessment Guides': 'credit_assessment',
  'Retirement': 'retirement',
  'Retirement Guides': 'retirement',
  'Understanding Taxes': 'tax',
  'Understanding Taxes Guides': 'tax',
  'Prospecting': 'prv_prospecting',
};

// ---------------------------------------------------------------------------
// Sidebar elementary item-name → ElementaryItemId mapping
// ---------------------------------------------------------------------------

export const ELEMENTARY_ITEM_NAME_TO_ID: Record<string, ElementaryItemId> = {
  'Allowance & Savings': 'allowance_savings',
  'Charitable Giving': 'charitable_giving',
  'Goal Setting': 'goal_setting',
  'Lemonade Stand': 'lemonade_stand',
  'Wants vs Needs': 'wants_needs',
};
